import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO component — sets <title>, description, canonical, and OpenGraph
 * (+ Twitter Card) tags for social sharing (WhatsApp, Threads, Twitter, etc.).
 *
 * Props:
 *   title        e.g. "Review SMA Kanisius Jakarta - Rating 4.7 | The School Reports"
 *   description  meta description (150-160 chars ideal)
 *   image        absolute or path URL for og:image
 *   type         "website" | "article" | "profile"
 *   canonical    absolute URL (defaults to current path on window.location.origin)
 *   robots       optional robots directive, e.g. "noindex,follow". Emitted ONLY
 *                when provided — routes that don't pass it get no robots tag,
 *                so default indexing behavior elsewhere is unchanged.
 */
const SITE_NAME = "The School Reports";
const DEFAULT_IMAGE = "/og-preview.png";

export default function SEO({
  title,
  description = "Review jujur orang tua & alumni untuk sekolah TK–SMA di Indonesia. Bandingkan biaya, akreditasi, dan kualitas akademik sebelum memutuskan.",
  image = DEFAULT_IMAGE,
  type = "website",
  canonical,
  robots,
}) {
  const finalTitle = title ? `${title}` : `${SITE_NAME} — Platform informasi pendidikan Indonesia`;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://theschoolreports.id";
  const path = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
  const finalCanonical = canonical || `${origin}${path}`;
  const finalImage = image?.startsWith("http") ? image : `${origin}${image}`;
  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={description} />
      {robots ? <meta name="robots" content={robots} /> : null}
      <link rel="canonical" href={finalCanonical} />
      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="id_ID" />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />
    </Helmet>
  );
}
